import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { WEBROUTES } from '@zonofi/zono-money-helpers';
import styles from './gallery-view-screen.module.scss';
import {
  ShareBar,
  FolderList,
  useFolderList,
  Masonry,
  FullscreenPreview,
} from '@zonofi/zono-money-catalog-tool-stack';
import { SVGS } from '@zonofi/zono-money-design-system';

export const GalleryViewScreen: React.FC = () => {
  const { folderId } = useParams();
  const [searchParams] = useSearchParams();
  const { folders, images, files, loading, error } = useFolderList();
  const navigate = useNavigate();
  const [selected, setSelected] = useState<string[]>([]);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);

  // Store zoom/pan state per image (by src)
  type ZoomPanState = {
    scale: number;
    translate: { x: number; y: number };
  };

  const [imageZoomPan, setImageZoomPan] = useState<
    Record<string, ZoomPanState>
  >({});
  const imageZoomPanRef = useRef<Record<string, ZoomPanState>>({});

  const createDefaultZoomPan = () => ({
    scale: 1,
    translate: { x: 0, y: 0 },
  });
  // Maintain refs for all selected image containers
  const selectedImageContainerRefs = useRef<Map<string, HTMLDivElement>>(
    new Map()
  );
  const id = searchParams.get('id');

  const imgs = (folderId ? images[folderId as keyof typeof images] : []) || [];
  const folderMeta = useMemo(() => {
    return folders?.find((f) => f.id === folderId);
  }, [folderId, folders]);

  // Ensure every image has a default zoom/pan entry so we can
  // safely reference stored state without altering base framing.
  useEffect(() => {
    if (!imgs.length) return;
    setImageZoomPan((prev) => {
      let mutated = false;
      const next = { ...prev };
      imgs.forEach((img) => {
        if (!img?.src || next[img.src]) {
          return;
        }
        const defaultState = createDefaultZoomPan();
        next[img.src] = defaultState;
        imageZoomPanRef.current[img.src] = defaultState;
        mutated = true;
      });
      return mutated ? next : prev;
    });
  }, [imgs]);

  const updateZoomPanForImage = useCallback(
    (src: string, updater: (current: ZoomPanState) => ZoomPanState) => {
      setImageZoomPan((prev) => {
        const current = prev[src] || createDefaultZoomPan();
        const updated = updater(current);
        imageZoomPanRef.current[src] = updated;
        if (
          current.scale === updated.scale &&
          current.translate.x === updated.translate.x &&
          current.translate.y === updated.translate.y
        ) {
          return prev;
        }
        return {
          ...prev,
          [src]: updated,
        };
      });
    },
    []
  );

  const handleToggleSelect = (src: string) => {
    setSelected((prev) => {
      const alreadySelected = prev.includes(src);
      if (alreadySelected) {
        // Remove ref when deselected
        selectedImageContainerRefs.current.delete(src);
        return prev.filter((s) => s !== src);
      }
      return [...prev, src];
    });
  };

  // Clean up refs when selection changes
  useEffect(() => {
    const currentSelected = new Set(selected);
    const refsToRemove: string[] = [];
    selectedImageContainerRefs.current.forEach((_, src) => {
      if (!currentSelected.has(src)) {
        refsToRemove.push(src);
      }
    });
    refsToRemove.forEach((src) => {
      selectedImageContainerRefs.current.delete(src);
    });
  }, [selected]);

  const openPreview = (startSrc?: string) => {
    if (startSrc) {
      const index = imgs.findIndex((i) => i.src === startSrc);
      setActiveIndex(index >= 0 ? index : 0);
    } else if (selected.length > 0) {
      const idx = imgs.findIndex((i) => i.src === selected[0]);
      setActiveIndex(idx >= 0 ? idx : 0);
    } else setActiveIndex(0);
    setIsPreviewOpen(true);
  };

  if (!folderId) {
    return (
      <div className="catalog-tool">
        <FolderList folders={folders} images={images} files={files} />
      </div>
    );
  }

  return (
    <div className="catalog-tool">
      <div className={styles.galleryView}>
        {!isPreviewOpen && (
          <div className={styles.headerBar}>
            <button
              onClick={() => {
                if (selected.length > 0) {
                  setSelected([]);
                } else {
                  const baseRoute = id
                    ? `${WEBROUTES.CATALOG_TOOL}?id=${id}`
                    : WEBROUTES.CATALOG_TOOL;
                  navigate(baseRoute);
                }
              }}
              className={styles.button}
              aria-label="Back"
            >
              <img src={selected.length > 0 ? SVGS.CloseIcon : SVGS.BackIcon} />
            </button>
            <div className={styles.headerContent}>
              {selected.length > 0 ? (
                <>
                  <div className={styles.headerTitle}>
                    {selected.length} Selected
                  </div>
                  <div className={styles.headerSub}>{folderMeta?.name}</div>
                </>
              ) : (
                <div className={styles.headerTitleRow}>
                  <img src={SVGS.FolderIcon} alt="folder" />
                  <div className={styles.titleTextGroup}>
                    <div className={styles.headerTitle}>{folderMeta?.name}</div>
                    <div className={styles.headerSub}>{imgs.length} items</div>
                  </div>
                </div>
              )}
            </div>
            <div className={styles.headerRight} />
          </div>
        )}

        {!isPreviewOpen && (
          <Masonry
            imgs={imgs}
            selected={selected}
            handleToggleSelect={handleToggleSelect}
            selectedImageContainerRefs={selectedImageContainerRefs}
            styles={styles}
          />
        )}

        {selected.length > 0 && (
          <ShareBar
            selectedImages={imgs.filter((img) => selected.includes(img.src))}
          />
        )}
        {imgs.length > 0 && !isPreviewOpen && (
          <button
            className={styles.previewFab}
            aria-label="Open full screen preview"
            onClick={() => openPreview()}
          >
            <img src={SVGS.CarouselViewIcon} alt="grid" />
          </button>
        )}
        {}
        <FullscreenPreview
          imgs={imgs}
          activeIndex={activeIndex}
          setActiveIndex={setActiveIndex}
          imageZoomPan={imageZoomPan}
          isPreviewOpen={isPreviewOpen}
          setIsPreviewOpen={setIsPreviewOpen}
          styles={styles}
          selected={selected}
          folderMeta={folderMeta}
          handleToggleSelect={handleToggleSelect}
          updateZoomPanForImage={updateZoomPanForImage}
        />
      </div>
    </div>
  );
};
